import { supabase } from "@/integrations/supabase/client";
import { buildAllLlms, type LlmsBundle, type LlmsData } from "@/lib/llmsContent";

const db = supabase as any;

/** Busca o catálogo ao vivo usado como fonte dos arquivos de IA. */
export async function fetchLlmsData(): Promise<LlmsData> {
  const [listings, launches, posts] = await Promise.all([
    db.from("listings")
      .select("id,title,slug,neighborhood,property_type,bedrooms,area_m2,price")
      .eq("is_active", true)
      .order("price", { ascending: false })
      .limit(600),
    db.from("launches")
      .select("id,name,slug,neighborhood,developer,price_min,price_max,delivery_date,description")
      .eq("is_active", true)
      .order("name", { ascending: true }),
    db.from("blog_posts")
      .select("title,slug,excerpt,tags,published_at")
      .eq("is_published", true)
      .order("published_at", { ascending: false })
      .limit(200),
  ]);

  return {
    listings: listings.data || [],
    launches: launches.data || [],
    posts: posts.data || [],
    generatedAt: new Date().toISOString(),
  };
}

export async function generateLlmsBundle(): Promise<{ bundle: LlmsBundle; data: LlmsData }> {
  const data = await fetchLlmsData();
  return { bundle: buildAllLlms(data), data };
}

export interface LlmsFileStatus {
  name: string;
  url: string;
  ok: boolean;
  size: number;
  contentType: string | null;
  headings: number;
  links: number;
  lastModified: string | null;
}

/** Valida os arquivos realmente servidos na raiz do site. */
export async function checkPublishedLlmsFiles(): Promise<LlmsFileStatus[]> {
  const names = ["llms.txt", "llms-full.txt", "llms-en.txt", "llms-full-en.txt"];
  return Promise.all(
    names.map(async (name) => {
      const url = `/${name}`;
      try {
        const res = await fetch(`${url}?t=${Date.now()}`);
        const text = res.ok ? await res.text() : "";
        return {
          name,
          url,
          ok: res.ok && text.trimStart().startsWith("#"),
          size: text.length,
          contentType: res.headers.get("content-type"),
          headings: (text.match(/^#{1,3} /gm) || []).length,
          links: (text.match(/\]\(https?:\/\//g) || []).length,
          lastModified: (text.match(/Last update: ([^\s]+)/) || [])[1] || null,
        };
      } catch {
        return { name, url, ok: false, size: 0, contentType: null, headings: 0, links: 0, lastModified: null };
      }
    }),
  );
}

export function downloadText(name: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}
